/*eslint-disable*/
/* eslint-disable react/prop-types */
/* eslint-disable quotes */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import { Google_Map_Key } from "config";

const Input = styled(TextField)(({ required }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'grey',
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: '#999',
    },
    '&.Mui-focused fieldset': {
      borderLeftWidth: 4,
      padding: '4px !important',
    },
    position: 'relative',
    '&::before': required ? {
      content: '""',
      position: 'absolute',
      left: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      height: '60%',
      width: '3px',
      backgroundColor: '#ff0000',
      borderRadius: '1px',
      zIndex: 1,
    } : {},
  },
  '& input': {
    color: 'grey',
    paddingLeft: required ? '16px' : '14px',
  },
}));

// Cache implementation
const predictionCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

function clearExpiredCache() {
  const now = Date.now();
  for (const [key, value] of predictionCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      predictionCache.delete(key);
    }
  }
}

// API function with proper cancellation
async function getPlacePredictionsNewAPI(input, signal = null) {
  // Clear expired cache entries periodically
  if (Math.random() < 0.1) { // 10% chance to cleanup
    clearExpiredCache();
  }

  // Check cache first
  const cacheKey = input.toLowerCase().trim();
  const cached = predictionCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  const myHeaders = new Headers();
  myHeaders.append("X-Goog-FieldMask", "places.id,places.formattedAddress,places.location,places.displayName,places.addressComponents,places.types");
  myHeaders.append("Content-Type", "application/json");

  const raw = JSON.stringify({
    textQuery: input,
    // Alternative: Use region code restriction
    regionCode: "za"  // South Africa country code
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
  };

  // Add signal to request options if provided
  if (signal) {
    requestOptions.signal = signal;
  }

  try {
    const response = await fetch(
      `https://places.googleapis.com/v1/places:searchText?key=${Google_Map_Key}`,
      requestOptions
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    const places = result.places || [];

    // Cache successful results
    predictionCache.set(cacheKey, {
      data: places,
      timestamp: Date.now()
    });

    return places;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request was aborted');
      throw error; // Re-throw abort errors
    }
    console.error('Places API error:', error);
    throw error;
  }
}
export default function GoogleMapsAutoComplete(props) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null); // ← Store timeout ID here
  const currentSearchIdRef = useRef(0); // ← Also fix the search ID issue

  // Generate a simple session token
  const generateSessionToken = useCallback(() => {
    return Math.random().toString(36).substring(2);
  }, []);

  const sessionToken = useRef(generateSessionToken());

  // Properly implemented debounced search function
  const performSearch = useCallback(async (searchTerm) => {
    const searchId = ++currentSearchIdRef.current;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      const results = await getPlacePredictionsNewAPI(
        searchTerm,
        abortControllerRef.current.signal
      );

      // Only update if this is the most recent search
      if (searchId === currentSearchIdRef.current) {
        setOptions(results);
      }
    } catch (error) {
      // Only handle errors if this wasn't aborted and is the most recent search
      if (error.name !== 'AbortError' && searchId === currentSearchIdRef.current) {
        console.error('Search error:', error);
        setOptions([]);
      }
    } finally {
      // Only update loading state if this is the most recent search
      if (searchId === currentSearchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Throttled search function
  const throttledSearch = useCallback((searchTerm) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      performSearch(searchTerm);
    }, 500); // 500ms debounce
  }, [performSearch]);

  // Main effect for handling input changes
  useEffect(() => {
    if (inputValue === "") {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    // Only search with 4+ characters
    if (inputValue.trim().length < 4) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    throttledSearch(inputValue);
  }, [inputValue, throttledSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ... rest of the component remains the same
  const handleChange = useCallback((event, newValue) => {
    if (newValue && newValue.id) {
      // Enhance the selected value with coordinates
      const enhancedValue = {
        ...newValue,
        coords: newValue.location ? {
          lat: newValue.location.latitude,
          lng: newValue.location.longitude,
        } : null,
        description: newValue.formattedAddress || newValue.displayName?.text || '',
      };
      props.onChange(enhancedValue);
    } else {
      props.onChange(newValue);
    }
  }, [props]);

  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Autocomplete
      style={props.style}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.formattedAddress || option.displayName?.text || ""
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      loading={isLoading}
      loadingText="Loading locations..."
      value={props.value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <Input
          {...params}
          required
          label={props.placeholder}
          variant="outlined"
          fullWidth
          helperText={
            inputValue.length > 0 && inputValue.length < 4
              ? "Enter at least 4 characters to search"
              : ""
          }
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs>
              <Typography variant="body2">
                {option.displayName?.text || ""}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {option.formattedAddress || ""}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
      noOptionsText={
        inputValue.length < 4
          ? "Type at least 4 characters to search"
          : "No locations found"
      }
    />
  );
}